<template>
  <div
    class="composer"
    :class="{ 'composer-read-only': !editable }"
    @composer-submit="onSubmit"
    @click="editable && focusEditor()"
  >
    <ProsemirrorAdapterProvider>
      <div class="composer-editor-wrapper">
        <div v-if="showPlaceholder && editable" class="composer-placeholder">
          <p class="empty">
            <slot name="placeholder">
              {{ placeholder }}
            </slot>
          </p>
        </div>
        <ProseMirrorEditor
          ref="editor"
          :doc="doc"
          :references-plugin-params="referencesPluginParams"
          :at-menu-plugin-params="atMenuPluginParams"
          :floating-menu-params="editable ? floatingMenuParams : undefined"
          :extra-plugins="composerPlugins"
          :enable-input-rules="editable"
          :enable-history="editable"
          :enable-base-keymap="false"
          :enable-drop-cursor="false"
          :enable-gap-cursor="false"
          :enable-node-range-selection="false"
          :enable-unique-ids="false"
          :enable-paste-url="editable"
          :editable="editable"
          class="composer-editor"
          @change="onChange"
        />
      </div>
    </ProsemirrorAdapterProvider>
  </div>
</template>

<script lang="ts">
import { ProsemirrorAdapterProvider } from "@prosemirror-adapter/vue";
import { toggleMark } from "prosemirror-commands";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import {
  computed,
  defineComponent,
  getCurrentInstance,
  PropType,
  provide,
  ref,
  shallowRef,
  watch,
} from "vue";
import { useRouter } from "vue-router";

import type { Document, OrgId, WorkspaceId } from "@ctrl/controller-rpc";

import assert from "@/assert";
import * as inject from "@/components/inject";
import { isAiIntegrationKey } from "@/components/integrations/aiIntegrationKeys";
import type { IntegrationCandidate } from "@/components/integrations/candidates";
import {
  isMcpIntegrationKey,
  mcpPackageIdFromIntegrationKey,
} from "@/components/integrations/mcpCandidates";
import { resolveIntegrationOrganizationIds } from "@/components/integrations/useIntegrations";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

import { getAtMenuPluginParams } from "./at-menu-list";
import { documentToProseMirrorDoc } from "./document-to-prosemirror";
import { getFloatingMenuParams } from "./formatting-menu";
import { createHyperlinkEditPopup } from "./hyperlink-edit-popup";
import { composerPlugin } from "./prosemirror/plugins/composer";
import { toggleLink } from "./prosemirror/plugins/floating-menu";
import {
  integrationCandidatesKey,
  integrationLookupKey,
  integrationServicesVersionKey,
  openConnectIntegrationKey,
  openIntegrationKey,
  openMcpIntegrationKey,
} from "./prosemirror/plugins/integrations";
import { keymap } from "./prosemirror/plugins/keymap";
import ProseMirrorEditor from "./prosemirror/ProseMirrorEditor.vue";
import schema from "./prosemirror/schema";
import { getReferencesPluginParams } from "./references-list";
import { parseMarkupToDoc, serializeDocFromState } from "./serialize";

export { serializeDoc } from "./serialize";

export type ComposerIntegrationMenuState = {
  candidates: IntegrationCandidate[];
  loading: boolean;
};

export default defineComponent({
  name: "Composer",
  components: { ProsemirrorAdapterProvider, ProseMirrorEditor },
  props: {
    value: { type: String, required: false, default: "" },
    document: {
      type: Object as PropType<Document | null>,
      required: false,
      default: null,
    },
    workspaceId: { type: String, required: true },
    /**
     * The organization whose objects the `[[` / `@` reference searches
     * offer:
     *
     * - an `OrgId`: search exactly that organization;
     * - `null`: search across all organizations (e.g. the assistant, which
     *   acts on the user's behalf everywhere);
     * - `undefined` (the default): derive it from `workspaceId`.
     */
    referenceSearchOrganizationId: {
      type: String as PropType<OrgId | null | undefined>,
      required: false,
      default: undefined,
    },
    referenceContextWorkspaceId: {
      type: String as PropType<WorkspaceId | null | undefined>,
      required: false,
      default: undefined,
    },
    integrationOrganizationIds: {
      type: Array as PropType<OrgId[] | undefined>,
      required: false,
      default: undefined,
    },
    mentionsEnabled: { type: Boolean, required: false, default: true },
    placeholder: { type: String, required: false, default: "" },
    editable: { type: Boolean, required: false, default: true },
  },
  emits: {
    submit: (_proseMirrorDoc: ProseMirrorNode) => true,
    change: (_value: string) => true,
    "integration-candidates-change": (_state: ComposerIntegrationMenuState) =>
      true,
  },
  setup(props, { emit, slots }) {
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const integrations = inject.integrations();
    const workspaceIdRef = computed(() => props.workspaceId);

    const editor = ref<InstanceType<typeof ProseMirrorEditor> | null>(null);
    const editorView = computed(() =>
      editor.value ? editor.value.editorView : null
    );

    const componentInstance = getCurrentInstance();
    assert(componentInstance, "Couldn't get the component instance");

    // The composer owns its editor state.
    // `props.document` takes precedence over `props.value` when provided.
    // Both are treated as external overrides, not as fully controlled values.
    const currentValue = shallowRef<string>(props.value);
    const doc = shallowRef(
      props.document
        ? documentToProseMirrorDoc(props.document, logger)
        : parseMarkupToDoc(props.value, logger)
    );
    watch(
      () => props.document,
      (d) => {
        if (d) doc.value = documentToProseMirrorDoc(d, logger);
      }
    );
    watch(
      () => props.value,
      (v) => {
        if (props.document) return;
        if (v === currentValue.value) return;
        doc.value = parseMarkupToDoc(v, logger);
        currentValue.value = v;
      }
    );

    const organizationId = computed<OrgId | undefined>(() =>
      props.workspaceId
        ? getOrganizationIdFromWorkspaceId(profile.value, props.workspaceId)
        : undefined
    );
    const integrationOrganizationIds = computed<readonly OrgId[] | undefined>(
      () =>
        resolveIntegrationOrganizationIds(
          props.integrationOrganizationIds,
          organizationId.value
        )
    );
    const integrationCandidateData = integrations.candidatesFor(
      integrationOrganizationIds
    );
    // AI-capable Membrane connections, plus enabled external MCP servers
    // (whose tools the agent can invoke directly).
    const integrationCandidates = computed<IntegrationCandidate[]>(() =>
      integrationCandidateData.candidates.value.filter(
        ({ integrationKey }) =>
          isAiIntegrationKey(integrationKey) ||
          isMcpIntegrationKey(integrationKey)
      )
    );
    watch(
      () =>
        [
          integrationCandidates.value,
          integrationCandidateData.loading.value,
        ] as const,
      ([candidates, loading]) => {
        emit("integration-candidates-change", {
          candidates: [...candidates],
          loading,
        });
      },
      { immediate: true }
    );
    const integrationLookup = integrationCandidateData.lookup;
    const integrationServicesVersion = integrationCandidateData.servicesVersion;
    provide(integrationCandidatesKey, integrationCandidates);
    provide(integrationLookupKey, integrationLookup);
    provide(integrationServicesVersionKey, integrationServicesVersion);
    const router = useRouter();
    const openConnectIntegration = (
      appId: string,
      serviceOrganizationId?: OrgId
    ) => {
      const app = integrationCandidateData.getServiceById(
        appId,
        serviceOrganizationId
      );
      const orgId =
        integrationCandidateData.getOrganizationIdByServiceId(
          appId,
          serviceOrganizationId
        ) ?? organizationId.value;
      if (!app?.key || !orgId) return;
      void navigateTo(RouteNames.SettingsMembraneIntegration, router, {
        params: { orgId, appKey: app.key },
        query: { addAccount: "true" },
      });
    };
    provide(openConnectIntegrationKey, openConnectIntegration);
    const openIntegration = (appId: string, serviceOrganizationId?: OrgId) => {
      const app = integrationCandidateData.getServiceById(
        appId,
        serviceOrganizationId
      );
      const orgId =
        integrationCandidateData.getOrganizationIdByServiceId(
          appId,
          serviceOrganizationId
        ) ?? organizationId.value;
      if (!app?.key || !orgId) return;
      void navigateTo(RouteNames.SettingsMembraneIntegration, router, {
        params: { orgId, appKey: app.key },
      });
    };
    provide(openIntegrationKey, openIntegration);
    const openMcpIntegration = (integrationKey: string) => {
      const packageId = mcpPackageIdFromIntegrationKey(integrationKey);
      const orgId = organizationId.value;
      if (packageId && orgId) {
        void navigateTo(RouteNames.SettingsMcpIntegration, router, {
          params: { orgId, packageId },
        });
        return;
      }
      // Custom (non-catalog) server, or no organization to scope the package
      // screen's URL: the Custom MCPs screen manages those and needs no params.
      void navigateTo(RouteNames.SettingsCustomMcpClients, router);
    };
    provide(openMcpIntegrationKey, openMcpIntegration);

    const referenceSearchOrganizationId = computed<OrgId | null>(() =>
      props.referenceSearchOrganizationId === undefined
        ? (organizationId.value ?? null)
        : props.referenceSearchOrganizationId
    );
    const referenceContextWorkspaceId = computed<WorkspaceId | null>(() =>
      props.referenceContextWorkspaceId === undefined
        ? props.workspaceId
        : props.referenceContextWorkspaceId
    );

    const referencesPluginParams = getReferencesPluginParams(
      controller,
      profile,
      componentInstance,
      editorView,
      referenceSearchOrganizationId,
      {
        workspaceId: workspaceIdRef,
        contextWorkspaceId: referenceContextWorkspaceId,
      }
    );

    // The composer's `@` menu offers people mentions, integrations, and
    // references — no date stamps.
    const atMenuPluginParams = getAtMenuPluginParams(
      profile,
      componentInstance,
      editorView,
      workspaceIdRef,
      {
        arePeopleEnabled: () => props.mentionsEnabled,
        integrations: { candidates: integrationCandidates },
        references: {
          controller,
          organizationId: referenceSearchOrganizationId,
        },
      }
    );

    const hyperlinkEditPopup = createHyperlinkEditPopup(
      editorView,
      componentInstance
    );
    const floatingMenuParams = getFloatingMenuParams(
      componentInstance,
      editorView,
      hyperlinkEditPopup
    );

    // When not editable, skip composer plugin (no Enter-to-submit) and keymaps.
    const composerPlugins = computed(() => {
      if (!props.editable) return [];
      return [
        keymap({
          "Mod-b": toggleMark(schema.marks.strong),
          "Mod-i": toggleMark(schema.marks.em),
          "Mod-u": toggleMark(schema.marks.underline),
          "Mod-k": toggleLink(),
          "Shift-Mod-c": toggleMark(schema.marks.code),
          "Shift-Mod-s": toggleMark(schema.marks.strikethrough),
        }),
        composerPlugin(),
      ];
    });

    // IMPORTANT: this must track the live v-model value, not the ProseMirror doc.
    const isEmpty = computed(
      () => String(currentValue.value || "").trim().length === 0
    );
    const showPlaceholder = computed(() => {
      if (!isEmpty.value) return false;
      if (slots.placeholder) return true;
      return !!props.placeholder;
    });

    const onChange = (state: EditorState) => {
      const v = serializeDocFromState(state);
      currentValue.value = v;
      emit("change", v);
    };

    const onSubmit = () => {
      if (!props.editable) return;
      const state = editorView.value?.state;
      if (!state) return;
      currentValue.value = serializeDocFromState(state);
      emit("submit", state.doc);
    };

    const setValue = (value: string) => {
      currentValue.value = value;
      doc.value = parseMarkupToDoc(value, logger);
    };

    const submit = () => {
      onSubmit();
    };

    const focusEditor = () => {
      editorView.value?.focus();
    };

    const focusEditorEnd = () => {
      // `selectEnd` also focuses the view.
      editor.value?.selectEnd();
    };

    const insertTextAtEnd = (text: string) => {
      editor.value?.selectEnd();
      const view = editorView.value;
      if (view) {
        view.dispatch(view.state.tr.insertText(text));
        view.focus();
      }
    };

    const getValue = (): string => {
      const state = editorView.value?.state;
      if (!state) return "";
      return serializeDocFromState(state);
    };

    return {
      editor,
      doc,
      referencesPluginParams,
      atMenuPluginParams,
      floatingMenuParams,
      composerPlugins,
      showPlaceholder,
      onChange,
      onSubmit,
      setValue,
      submit,
      focusEditor,
      focusEditorEnd,
      insertTextAtEnd,
      getValue,
    };
  },
});
</script>

<style scoped lang="scss">
.composer {
  width: 100%;
  position: relative;
}

.composer-editor-wrapper {
  position: relative;
  width: 100%;
}

.composer-read-only .composer-editor {
  cursor: default;
}

.composer-placeholder {
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  padding: 4px 10px;
  pointer-events: none;

  // Match TemplateSelector empty state styling
  p.empty {
    margin: 0;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-40);
    }

    // Slot content is compiled in the parent scope, so we must use `:deep`
    // to style the hotkey chip <span> elements.
    :deep(span) {
      display: inline-block;
      padding: 3px 6px;
      border-radius: 2px;
      @extend %font-caption-regular;
      @include themed() {
        background-color: t($bg);
        border: 1px solid t($grey-15);
      }
    }
  }
}
</style>
